<template>
  <div class="scroller">
    <div class="resource">
      <header class="resource__header">
        <h2 class="resource__heading">
          <template v-if="action === 'create'">Create new</template>
          <template v-else>Edit</template>
        </h2>

        <div class="languages">
          <img v-for="(globalLanguage, index) in languages" :key="index" :src="`/static/flags/${globalLanguage.iso}.png`" class="languages__image" @click="changeOrAddLanguage(globalLanguage.iso)" :class="{'languages__image--active': language == globalLanguage.iso }">
        </div>
      </header>

      <form class="form" @submit.prevent="onSubmit">
        <div class="grid">
          <div :class="{ 'grid__column--eight': type.name != 'Sidebar', 'grid__column--twelve': type.name === 'Sidebar' }">
            <div class="form__group">
              <label class="form__label" for="title">Title:</label>
              <span class="form__error" v-if="errors.has('title')">{{ errors.first('title') }}</span>
              <input type="text" id="title" name="title" class="form__input" :class="{ 'form__input--error': errors.has('title') }" v-validate="{ required: true }"
                v-model="resource.languages[language].title"
                @blur="resource.languages[language].url.length === 0 && resource.languages[language].title.length > 0 ? generateSeoTitle() : null" />
            </div>

            <div class="form__group--url" v-if="type.name != 'Sidebar'">
              <span class="form__error" v-if="errors.has('url')">{{ errors.first('url') }}</span>
              <input
                type="text" id="url" name="url" ref="url"
                class="form__input" :class="{ 'form__input--error': errors.has('url') }"
                v-validate="{ required: true }"
                :value="resource.languages[language].url"
                @input="slugifyOnTyping($event.target.value)"
              />
              <button type="button" class="btn btn--primary btn--url" @click="generateSeoTitle">Regenerate</button>
            </div>

            <div class="form__group" v-if="type.name != 'Sidebar'">
              <label class="form__label" for="subtitle">Subtitle:</label>
              <input type="text" id="subtitle" name="subtitle" class="form__input" v-model="resource.languages[language].additionals.subtitle" />
            </div>

            <div class="grid">
              <div class="grid__column--six">
                <div class="formToggle" v-if="type.name != 'Sidebar'">
                  <input class="formToggle__checkbox" type="checkbox" name="news_on_homepage" id="news_on_homepage" :value="true" v-model="resource.additionals.news_on_homepage">
                  <label class="formToggle__label formToggle__label--large" for="news_on_homepage"><span>ON/OFF</span> show in homepage news</label>
                </div>
              </div>
              <div class="grid__column--six">
                <div class="formToggle" v-if="type.name != 'Sidebar'">
                  <input class="formToggle__checkbox" type="checkbox" name="special_offer_on_homepage" id="special_offer_on_homepage" :value="true" v-model="resource.additionals.special_offer_on_homepage">
                  <label class="formToggle__label formToggle__label--large" for="special_offer_on_homepage"><span>ON/OFF</span> show in homepage special offers</label>
                </div>
              </div>
              <div class="grid__column--six">
                <div class="formToggle" v-if="type.name != 'Sidebar'">
                  <input class="formToggle__checkbox" type="checkbox" name="service_on_homepage" id="service_on_homepage" :value="true" v-model="resource.additionals.service_on_homepage">
                  <label class="formToggle__label formToggle__label--large" for="service_on_homepage"><span>ON/OFF</span> show in homepage services</label>
                </div>
              </div>
            </div>

            <div class="mapPicker" v-if="type.name === 'Stores'">
              <nav class="mapPicker__Tabs">
                <ul>
                  <li><a @click="setFloor(1)" :class="{ 'is-Active': floor === 1 }">Ground</a></li>
                  <li><a @click="setFloor(2)" :class="{ 'is-Active': floor === 2 }">Floor</a></li>
                </ul>
              </nav>
              <div class="mapPicker__Maps">
                <div class="mapPicker__Map" :class="{ 'is-Active': floor === 1 }" v-html="require('@/assets/map/map_ground.svg')"></div>
                <div class="mapPicker__Map" :class="{ 'is-Active': floor === 2 }" v-html="require('@/assets/map/map_floor.svg')"></div>
              </div>
              <div class="mapPicker__Selected">
                <p v-if="resource.additionals.map">
                  <span @click="setArea(item)" v-for="item in resource.additionals.map.areas" :key="item"><strong>ID:</strong> {{ item }}</span>
                </p>
                <button class="btn btn--extraSmall btn--danger" type="button" @click="resetArea">Reset all</button>
              </div>
            </div>

            <PageBuilder :data.sync="resource" :action="action" :stores="stores" :language="language"/>

            <div class="form__group">
              <button type="submit" class="btn btn--primary mt1" v-if="action === 'create'">Publish</button>
              <button type="submit" class="btn btn--primary mt1" v-else>Update</button>
              <button type="button" class="btn btn--danger"  v-if="action === 'edit'" @click="deleteLanguage">Delete language</button>
            </div>
          </div>
          <div class="grid__column--four" v-if="type.name != 'Sidebar'">
            <div class="form__group">
              <label class="form__label" for="type_id">Type:</label>
              <span class="form__error" v-if="errors.has('type_id')">{{ errors.first('type_id') }}</span>
              <select name="type_id" id="type_id" v-model="resource.type_id" :class="{ 'form__input--error': errors.has('type_id') }" v-validate="{ required: true }">
                <option :value="type.id" v-for="type in types" :key="type.id">{{ type.name }}</option>
              </select>
            </div>
            <div class="form__group">
              <label class="form__label" for="status">Status:</label>
              <select name="status" id="status" v-model="resource.status">
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="private">Private</option>
              </select>
            </div>

            <div class="form__group" v-if="type.name !== 'Events'">
              <label class="form__label" for="title">Published at:</label>
              <datetime type="datetime" v-model="resource.published_at" zone="UTC"></datetime>
            </div>

            <div class="form__group" v-if="type.name === 'Events'">
              <label class="form__label" for="title">Event start date:</label>
              <span class="form__error" v-if="errors.has('event_start_date')">{{ errors.first('event_start_date') }}</span>
              <datetime
                id="event_start_date" name="event_start_date"
                :class="{ 'form__input--error': errors.has('event_start_date') }"
                v-validate="{ required: true }"
                type="datetime"
                v-model="resource.additionals.event_start_date"
                zone="UTC"
              />
            </div>

            <div class="form__group" v-if="type.name === 'Events'">
              <label class="form__label" for="title">Event end date:</label>
              <span class="form__error" v-if="errors.has('event_end_date')">{{ errors.first('event_end_date') }}</span>
              <datetime
                id="event_end_date" name="event_end_date"
                :class="{ 'form__input--error': errors.has('event_end_date') }"
                v-validate="{ required: true }"
                type="datetime"
                v-model="resource.additionals.event_end_date"
                zone="UTC"
              />
            </div>

            <div class="form__group">
              <label class="form__label" for="seoTitle">Seo title:</label>
              <input type="text" id="seoTitle" name="seoTitle" class="form__input" v-model="resource.languages[language].seoTitle" />
            </div>

            <div class="form__group">
              <label class="form__label" for="seoDescription">Seo description:</label>
              <input type="text" id="seoDescription" name="seoDescription" class="form__input" v-model="resource.languages[language].seoDescription" />
            </div>

            <div class="form__group">
              <label class="form__label" for="sidebar_left">Sidebar left:</label>
              <select name="sidebar_left" id="sidebar_left" v-model="resource.sidebar_left_id">
                <option :value="null">No sidebar</option>
                <option :value="sidebar.id" v-for="sidebar in sidebars" :key="sidebar.id">{{ sidebar.title }}</option>
              </select>
            </div>

            <div class="form__group">
              <label class="form__label" for="sidebar_right">Sidebar right:</label>
              <select name="sidebar_right" id="sidebar_right" v-model="resource.sidebar_right_id">
                <option :value="null">No sidebar</option>
                <option :value="sidebar.id" v-for="sidebar in sidebars" :key="sidebar.id">{{ sidebar.title }}</option>
              </select>
            </div>

            <div class="from__group" v-if="type.name === 'Stores'">
              <label class="form__label" for="seoDescription">Telephone:</label>
              <input type="text" id="seoDescription" name="seoDescription" class="form__input" v-model="resource.additionals.tel" />
            </div>

            <div class="from__group" v-if="type.name === 'Stores'">
              <label class="form__label" for="seoDescription">Email:</label>
              <input type="text" id="seoDescription" name="seoDescription" class="form__input" v-model="resource.additionals.email" />
            </div>

            <div class="from__group" v-if="type.name === 'Stores'">
              <label class="form__label" for="seoDescription">Website:</label>
              <input type="text" id="seoDescription" name="seoDescription" class="form__input" v-model="resource.additionals.website" />
            </div>

            <div class="form__group">
              <UploadImage :image.sync="resource.languages[language].featured_image"/>
            </div>

             <div class="form__group">
              <UploadImage :image.sync="resource.languages[language].listing_image" text="Listing image"/>
            </div>

            <div class="form__group" v-if="type.name === 'Stores'">
              <UploadImage :image.sync="resource.logo" text="Logo"/>
            </div>

            <ul class="scrollBox__header" v-if="type.name === 'Offers'">
              <li class="scrollBox__type">Related stores:</li>
            </ul>
            <ul class="scrollBox__list" v-if="type.name === 'Offers'">
              <li class="scrollBox__item" v-for="store in stores" :key="store.id">
                <label class="scrollBox__label">
                  <input type="checkbox" class="scrollBox__checkbox" :value="store.id" v-model="resource.additionals.stores"> {{ store.title }}
                </label>
              </li>
            </ul>

            <ul class="scrollBox__header">
              <li class="scrollBox__type">Categories</li>
            </ul>
            <ul class="scrollBox__list">
              <li class="scrollBox__item" v-for="category in categories" :key="category.id">
                <label class="scrollBox__label">
                  <input type="checkbox" class="scrollBox__checkbox" :value="category" v-model="resource.categories"> {{ category.languages[language].title }}
                </label>
              </li>
            </ul>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import * as types from '@/store/types.js'
import Category from '@/model/Category'
import Page from '@/model/Page'
import UploadImage from '@/components/Utils/UploadImage'
import PageBuilder from '@/components/Utils/PageBuilder'
import { slugify, slugifyString, submitResource } from '@/helpers/handleForm.js'
import { loadResource } from '@/helpers/resource'

export default {
  components: { UploadImage, PageBuilder },
  computed: {
    languageGlobal () {
      return this.$store.getters[types.ACTIVE_LANGUAGE]
    },
    defaultLangauge () {
      return this.$store.getters[types.DEFAULT_LANGUAGE]
    },
    languages () {
      return this.$store.getters[types.LANGUAGES]
    },
    categories () {
      return this.$store.getters[types.ALL_CATEGORIES]
    },
    sliders () {
      return this.$store.getters[types.ALL_SLIDERS]
    },
    types () {
      return this.$store.getters[types.ALL_TYPES]
    },
    type () {
      this.$store.dispatch(types.ACTIVE_TYPE)
      return this.$store.getters[types.ACTIVE_TYPE]
    }
  },
  props: {
    action: {
      type: String,
      default: 'edit'
    },
    model: {
      required: true
    },
    resource: {
      type: Object,
      default () {
        return {
          status: 'published',
          published_at: new Date().toISOString(),
          categories: [],
          tags: [],
          type_id: this.$route.params.typeId,
          languages: {},
          additionals: {
            subtitle: '',
            tel: '',
            email: '',
            website: '',
            map: {
              areas: [],
              level: ''
            },
            stores: []
          }
        }
      }
    }
  },
  data () {
    return {
      floor: 1,
      area: '',
      show: false,
      sidebars: [],
      stores: [],
      language: ''
    }
  },
  created () {
    this.language = this.languageGlobal

    if (this.action === 'create') {
      this.$set(this.resource.languages, this.language, this.languageDefaults(this.language))
    }

    if (this.type.name === 'News' && this.action !== 'edit') {
      this.resource.additionals.news_on_homepage = true
    }

    if (this.type.name === 'Offers' && this.action !== 'edit') {
      this.resource.additionals.special_offer_on_homepage = true
    }

    if (this.type.name === 'Fun & Services' && this.action !== 'edit') {
      this.resource.additionals.service_on_homepage = true
    }

    loadResource(Category, types.GET_ALL_CATEGORIES, false, {all_languages: true, sort_by: 'asc'})

    Page.index({allPages: true, types: ['Sidebar']}).then((response) => {
      this.sidebars = response.data.data
    })

    Page.index({allPages: true, types: ['Stores'], order_by: 'title', sort_by: 'asc'}).then((response) => {
      this.stores = response.data.data
    })

    setTimeout(() => {
      this.show = true
    }, 501)
  },
  methods: {
    onSubmit () {
      if (this.type.name === 'Events') {
        this.resource.published_at = this.resource.additionals.event_start_date
      }

      submitResource(this.$validator, this.model, this.resource, this.action)
    },
    generateSeoTitle () {
      slugify(this.resource, types.UPDATE_PAGE_URL, this.action, this.language)
    },
    slugifyOnTyping (value) {
      this.resource.languages[this.language].url = slugifyString(value)
    },
    changeOrAddLanguage (language) {
      if (!this.resource.languages[language]) {
        this.$set(this.resource.languages, language, this.languageDefaults(language))
      }
      this.language = language
    },
    languageDefaults (language) {
      return {
        title: '',
        url: '',
        subtitle: '',
        additionals: {},
        blocks: [],
        language: language
      }
    },
    setFloor (floor) {
      this.floor = floor
    },
    setArea (val) {
      document.getElementById(val).classList.remove('is-Active')
      document.getElementById(val).classList.remove('is-Populated')

      if (this.resource.additionals.map.areas.includes(val)) {
        const idx = this.resource.additionals.map.areas.indexOf(val)
        this.resource.additionals.map.areas.splice(idx, 1)
      } else {
        this.resource.additionals.map.areas.push(val)
        document.getElementById(val).classList.add('is-Active')
      }
      this.resource.additionals.map.level = this.floor.toString()
    },
    resetArea () {
      this.resource.additionals.map.areas = []
      Array.from(document.getElementsByTagName('path')).forEach(item => {
        item.classList.remove('is-Active')
        item.classList.remove('is-Populated')
      })
    },
    initMap (click = true) {
      if (this.resource.additionals.map) {
        if (Object.keys(this.resource.additionals.map).length === 0) this.resource.additionals.map = Object.assign({}, this.resource.additionals.map, { areas: [], level: '' })
        if (this.resource.additionals.map.level.length > 0) this.floor = parseInt(this.resource.additionals.map.level)

        this.resource.additionals.map.areas.forEach(area => {
          let element = null
          if (area) element = document.getElementById(area)
          if (element) element.classList.add('is-Populated')
        })

        Array.from(document.getElementsByTagName('path')).forEach(item => {
          item.addEventListener('click', () => {
            this.setArea(item.id)
          })
        })
      }
    },
    deleteLanguage () {
      swal({
        title: 'Please confirm this action',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.value) {
          this.$delete(this.resource.languages, this.language)
          this.language = Object.keys(this.resource.languages)[0]
          this.onSubmit()
        }
      })
    }
  },
  watch: {
    'language' () {
      this.initMap()
    }
  },
  destroyed () {
    Array.from(document.getElementsByTagName('path')).forEach(item => {
      item.removeEventListener('click', () => {
        this.setArea(item.id)
      })
    })
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Utils/PageForm.vue